import { useTranslation } from "react-i18next";
import { AssetDetailsDTO, AssetKey } from "../../dto/AssetDTO";
import { ReactComponent as FileIcon } from "./assets/file.svg";

interface AssetDetailsPaneProps {
  asset?: AssetDetailsDTO;
  folderName?: string;
}

const formatUpdatedAt = (iso: string) => {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
};

/**
 * The file explorer's details column on the shared 400px pattern: preview and
 * the metadata the DTO actually carries — type, folder, updated by. (The
 * frames also draw file size and dimensions; the API does not expose them.)
 */
export default function AssetDetailsPane(props: AssetDetailsPaneProps) {
  const { asset, folderName } = props;
  const { t } = useTranslation();

  const paneClass =
    "w-[400px] shrink-0 border-l border-creator-border bg-creator-primary-background p-[16px] flex flex-col overflow-y-auto pit-quiet-scrollbar";

  if (!asset) return <div className={paneClass} />;

  const thumbnail = asset.variants[0]?.downloadUri;
  const showImage = asset.key === AssetKey.Image && thumbnail;

  const metaRow = (label: string, value: string) => (
    <div className="flex items-center justify-between gap-[12px] py-[9px] border-t border-creator-border">
      <span className="text-[11px] text-creator-text-sub">{label}</span>
      <span className="text-[11px] text-creator-text text-right truncate">
        {value}
      </span>
    </div>
  );

  return (
    <div className={paneClass} data-testid="asset-details-pane">
      <div className="text-[15px] font-semibold text-creator-text truncate">
        {asset.name}
      </div>

      <div className="mt-[12px] h-[230px] shrink-0 rounded-[8px] border border-creator-border bg-creator-surface flex items-center justify-center overflow-hidden">
        {showImage ? (
          <img
            src={thumbnail}
            alt=""
            className="object-contain max-w-full max-h-full"
          />
        ) : (
          <FileIcon className="assetcard-loading-icon w-[44px] h-[44px]" />
        )}
      </div>

      <div className="mt-auto pt-[16px]">
        {metaRow(t("file_explorer_ui.type"), t(AssetKey[asset.key].toLowerCase()))}
        {folderName && metaRow(t("file_explorer_ui.folder"), folderName)}
        {asset.updatedBy &&
          metaRow(
            t("file_explorer_ui.updated_by"),
            `${asset.updatedBy}${
              asset.updatedAt ? " · " + formatUpdatedAt(asset.updatedAt) : ""
            }`
          )}
      </div>
    </div>
  );
}
